import {
  createSlice,
  createListenerMiddleware,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { STORAGE_KEY } from 'src/components/NewRenterInterface/NavBar/NavBarConstants';
import { type RenterColorTheme } from 'src/theme/renterTheme';

const COLOR_THEME_STORAGE_KEY = 'renter-color-theme';

interface newRenterUIState {
  expanded: boolean;
  topBarHeight: number;
  bottomNavHeight: number;
  toolbarOffset: number;
  colorTheme: RenterColorTheme;
}

const getInitialExpanded = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === null ? true : stored === 'true';
};

const getInitialColorTheme = (): RenterColorTheme => {
  if (typeof window === 'undefined') {
    return 'default';
  }
  const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
  if (
    stored === 'orangeYellow' ||
    stored === 'oliveOrange' ||
    stored === 'tealSlate'
  ) {
    return stored;
  }
  return 'default';
};

const initialState: newRenterUIState = {
  expanded: getInitialExpanded(),
  topBarHeight: 0,
  bottomNavHeight: 0,
  toolbarOffset: 0,
  colorTheme: getInitialColorTheme(),
};

const NewRentersInterfaceUIManagementSlice = createSlice({
  name: 'newRenterUI',
  initialState,
  reducers: {
    toggleExpanded: (state) => {
      state.expanded = !state.expanded;
    },
    setTopBarHeight: (state, action: PayloadAction<number>) => {
      state.topBarHeight = action.payload;
    },
    setBottomNavHeight: (state, action: PayloadAction<number>) => {
      state.bottomNavHeight = action.payload;
    },
    setToolbarOffset: (state, action: PayloadAction<number>) => {
      state.toolbarOffset = action.payload;
    },
    setColorTheme: (state, action: PayloadAction<RenterColorTheme>) => {
      state.colorTheme = action.payload;
    },
  },
});

export const newRenterUIListenerMiddleware = createListenerMiddleware();

newRenterUIListenerMiddleware.startListening({
  matcher: isAnyOf(NewRentersInterfaceUIManagementSlice.actions.toggleExpanded),
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as { newRenterUI: newRenterUIState };
    localStorage.setItem(STORAGE_KEY, String(state.newRenterUI.expanded));
  },
});

newRenterUIListenerMiddleware.startListening({
  matcher: isAnyOf(NewRentersInterfaceUIManagementSlice.actions.setColorTheme),
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as { newRenterUI: newRenterUIState };
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, state.newRenterUI.colorTheme);
  },
});

export const {
  toggleExpanded,
  setTopBarHeight,
  setBottomNavHeight,
  setToolbarOffset,
  setColorTheme,
} = NewRentersInterfaceUIManagementSlice.actions;

export default NewRentersInterfaceUIManagementSlice.reducer;
